import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useEffect, useMemo, useState } from 'react';
import { fetchCategories, updatePortalListingCategories } from '../../services/api';
import { buildPortalListingHref } from '../../utils/portalUrls';

function normalizeTier(tier) {
  const value = String(tier || '').trim().toLowerCase();
  if (value === 'featured' || value === 'spotlight') return value;
  return 'basic';
}

export function getSecondaryCategoryLimit() {
  return 3;
}

function sortCategories(a, b) {
  return (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0)
    || String(a?.name || '').localeCompare(String(b?.name || ''));
}

function flattenCategories(categories) {
  const byId = new Map();

  const visit = (category) => {
    if (!category?.id || byId.has(category.id)) return;
    byId.set(category.id, {
      id: category.id,
      name: category.name || category.coreCategory?.name || 'Untitled category',
      parentId: category.parentId || null,
      isActive: category.isActive ?? category.isVisible ?? true,
      sortOrder: category.sortOrder ?? category.coreCategory?.sortOrder ?? 0,
    });

    if (Array.isArray(category.children)) {
      category.children.forEach((child) => visit({
        ...child,
        parentId: child?.parentId || category.id,
      }));
    }
  };

  (Array.isArray(categories) ? categories : []).forEach(visit);
  return Array.from(byId.values()).filter((category) => category.isActive !== false);
}

function CategoryUpgradeCallout({ listingId }) {
  const upgradeHref = listingId ? buildPortalListingHref(listingId, 'upgrade') : '/advertise/spotlight';

  return (
    <div className="rounded-lg border border-[color:color-mix(in_srgb,var(--primary)_22%,transparent)] bg-[color:color-mix(in_srgb,var(--primary)_6%,white)] p-3">
      <p className="text-sm font-semibold text-gray-900">Get more exposure! Upgrade Your Listing Now</p>
      <a className="mt-2 inline-flex text-sm font-semibold text-[var(--primary)] hover:underline" href={upgradeHref}>
        View upgrade options
      </a>
    </div>
  );
}

export default function ListingCategoryEditor({
  listingId,
  currentPrimaryCategoryId = null,
  currentSecondaryCategoryIds = [],
  tier = 'basic',
  onSaved,
  onCancel,
}) {
  const toast = useFeedback();
  const normalizedTier = normalizeTier(tier);
  const secondaryLimit = getSecondaryCategoryLimit(normalizedTier);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);


  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState(currentPrimaryCategoryId || '');
  const [selectedSecondaryCategoryIds, setSelectedSecondaryCategoryIds] = useState(
    Array.isArray(currentSecondaryCategoryIds) ? currentSecondaryCategoryIds.slice(0, secondaryLimit) : [],
  );
  const [serviceCategorySearch, setServiceCategorySearch] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setLoadError('');

    fetchCategories()
      .then((payload) => {
        if (!ignore) {
          setCategories(flattenCategories(payload));
        }
      })
      .catch(() => {
        if (!ignore) setLoadError('Unable to load categories. Please try again.');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  const parentCategories = useMemo(
    () => categories.filter((category) => !category.parentId).sort(sortCategories),
    [categories],
  );
  const secondaryCategories = useMemo(
    () => categories
      .filter((category) => category.parentId === selectedParentCategoryId)
      .sort(sortCategories),
    [categories, selectedParentCategoryId],
  );
  const filteredServiceCategories = useMemo(() => {
    const query = serviceCategorySearch.trim().toLowerCase();
    if (!query) return secondaryCategories;
    return secondaryCategories.filter((category) => String(category.name || '').toLowerCase().includes(query));
  }, [secondaryCategories, serviceCategorySearch]);

  const selectedParentCategory = parentCategories.find((category) => category.id === selectedParentCategoryId) || null;
  const selectedServiceCategories = useMemo(() => {
    const selectedIds = new Set(selectedSecondaryCategoryIds);
    return secondaryCategories.filter((category) => selectedIds.has(category.id));
  }, [secondaryCategories, selectedSecondaryCategoryIds]);
  const shouldShowUpgradeCallout = normalizedTier !== 'spotlight';
  const selectionCountLabel = `${selectedSecondaryCategoryIds.length} of ${secondaryLimit} selected`;
  const publicVisibilityText = normalizedTier === 'basic'
    ? 'Saved service categories support tenant filtering and sales workflows. Only the primary category is shown publicly on Basic listings.'
    : normalizedTier === 'featured'
      ? 'Saved service categories support tenant filtering and sales workflows. One service category is shown publicly on Featured listings.'
      : 'Choose up to 3 service categories. All selected service categories can be shown publicly on Spotlight listings.';

  const validate = () => {
    if (!selectedParentCategoryId) {
      return 'Please choose a primary category.';
    }
    if (!selectedParentCategory) {
      return 'Primary category must be a parent category.';
    }

    const selectedSecondarySet = new Set(selectedSecondaryCategoryIds);
    const availableSecondaryIds = new Set(secondaryCategories.map((category) => category.id));
    if ([...selectedSecondarySet].some((id) => !availableSecondaryIds.has(id))) {
      return 'Service categories must belong to the selected primary category.';
    }
    if (selectedSecondaryCategoryIds.length > secondaryLimit) {
      return `You can select up to ${secondaryLimit} service categor${secondaryLimit === 1 ? 'y' : 'ies'} on the ${normalizedTier[0].toUpperCase()}${normalizedTier.slice(1)} plan.`;
    }

    return '';
  };

  const handleParentChange = (event) => {
    const nextParentId = event.target.value;
    if (nextParentId === selectedParentCategoryId) return;

    // TODO: Add confirmation before clearing selected service categories when changing primary category.
    setSelectedParentCategoryId(nextParentId);
    setSelectedSecondaryCategoryIds([]);
    setServiceCategorySearch('');
    setValidationError('');


  };

  const toggleSecondaryCategory = (categoryId) => {
    if (secondaryLimit === 0) return;


    setValidationError('');
    setSelectedSecondaryCategoryIds((current) => {
      if (current.includes(categoryId)) {
        return current.filter((id) => id !== categoryId);
      }
      if (current.length >= secondaryLimit) {
        return current;
      }
      return [...current, categoryId];
    });
  };

  const removeSecondaryCategory = (categoryId) => {


    setValidationError('');
    setSelectedSecondaryCategoryIds((current) => current.filter((id) => id !== categoryId));
  };

  const save = async (event) => {
    event.preventDefault();
    const nextValidationError = validate();
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    const secondaryIdsToSave = selectedSecondaryCategoryIds;

    try {
      setSaving(true);

      setValidationError('');

      const result = await updatePortalListingCategories(listingId, {
        primaryCategoryId: selectedParentCategoryId,
        secondaryCategoryIds: secondaryIdsToSave,
      });
      toast.success('Categories updated.', { id: 'action:ListingCategoryEditor:save' + ':' + (listingId || '') });
      await onSaved?.(result);
    } catch {
      toast.error('Unable to save categories. Please try again.', { id: 'action:ListingCategoryEditor:save' + ':' + (listingId || '') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-600">Loading categories...</p>;
  }

  if (loadError) {
    return <InlineFeedback variant="error" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{loadError}</InlineFeedback>;
  }

  if (categories.length === 0) {
    return <p className="text-sm text-gray-600">No categories are available yet.</p>;
  }

  return (
    <form className="flex min-h-[70vh] flex-col" onSubmit={save}>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-5">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-900" htmlFor="listing-primary-category">
            Primary Category
          </label>
          <select
            id="listing-primary-category"
            value={selectedParentCategoryId}
            onChange={handleParentChange}
            className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
          >
            <option value="">Search or select a main category...</option>
            {parentCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <p className="text-xs leading-5 text-gray-500">Choose the main category where this business should appear.</p>
        </div>

        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Service Categories</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">{publicVisibilityText}</p>
            </div>
            <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
              {selectionCountLabel}
            </span>
          </div>

          {!selectedParentCategoryId ? (
            <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              Select a primary category first to choose service categories.
            </p>
          ) : secondaryCategories.length === 0 ? (
            <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              No service categories are available for this primary category yet.
            </p>
          ) : (
            <div className="space-y-4">
              {selectedServiceCategories.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-900">
                    Selected: {selectedServiceCategories.length} of {secondaryLimit}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedServiceCategories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => removeSecondaryCategory(category.id)}
                        className="rounded-full border border-[var(--primary)] bg-[color:color-mix(in_srgb,var(--primary)_8%,white)] px-3 py-2 text-sm font-semibold text-[var(--primary)] transition hover:bg-[color:color-mix(in_srgb,var(--primary)_12%,white)]"
                        aria-label={`Remove ${category.name}`}
                      >
                        {category.name} x
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900" htmlFor="service-category-search">
                  Available Service Categories
                </label>
                <input
                  id="service-category-search"
                  type="search"
                  value={serviceCategorySearch}
                  onChange={(event) => setServiceCategorySearch(event.target.value)}
                  placeholder="Search service categories..."
                  className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
                />
              </div>

              {filteredServiceCategories.length === 0 ? (
                <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
                  No matching service categories.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {filteredServiceCategories.map((category) => {
                    const selected = selectedSecondaryCategoryIds.includes(category.id);
                    const disabled = !selected && selectedSecondaryCategoryIds.length >= secondaryLimit;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        disabled={disabled || saving}
                        aria-pressed={selected}
                        onClick={() => toggleSecondaryCategory(category.id)}
                        className={`rounded-full border px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30 ${
                          selected
                            ? 'border-[var(--primary)] bg-[color:color-mix(in_srgb,var(--primary)_10%,white)] text-[var(--primary)]'
                            : disabled
                              ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400 opacity-60'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {selected ? 'Selected: ' : ''}{category.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {shouldShowUpgradeCallout ? <CategoryUpgradeCallout listingId={listingId} /> : null}

        {validationError ? (
          <InlineFeedback variant="error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {validationError}
          </InlineFeedback>
        ) : null}


      </div>

      <div className="sticky bottom-0 -mx-5 border-t border-gray-200 bg-white px-5 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-medium text-gray-500">{selectionCountLabel}</div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !selectedParentCategoryId}
              className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Categories'}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
